/**
 * ProShip Direct v2 — Product Master.
 *
 * The merchant's own catalogue: SKU codes, brand, weight, dimensions, HSN, MRP and images. It
 * exists in the admin panel already; this is the same collection, scoped to one merchant and read
 * by the person who owns it.
 *
 * NOT A FIXTURE. Unlike the rest of this module, every read and write here hits real endpoints that
 * already exist in the merchant panel:
 *   read    tools/generic/search on collection `product_master`, filtered to this merchant
 *   write   product/uploadSkuDetails?merchantId=… — a sheet upsert keyed on merchantId + skuCode
 *   images  /tools/uploadImagesNonExpire, whose URL the sheet then carries in image1..image10
 *
 * WHAT CHANGES FROM THE ADMIN VERSION, and it is mostly subtraction: the merchant filter and the
 * Merchant column go, because there is exactly one merchant here and it is the person reading, and
 * the upload form loses its merchant picker for the same reason.
 *
 * What is added is a way in for a merchant with three SKUs rather than three hundred: a manual
 * add-and-edit drawer, writing through the same sheet upsert the bulk file uses.
 *
 * NO COMPLETENESS COLUMN. An earlier version flagged products missing weight, dimensions or HSN.
 * That reads as a fault list, and it is not one: the channel fills those in when an order for the
 * SKU is received, so a blank row is usually a product that has not shipped yet. Sending a merchant
 * to fix something that fixes itself is worse than staying quiet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Grid, Typography, Button, TextField, Chip, Tooltip, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, CircularProgress, Alert, Collapse,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddOutlinedIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import ProzoDataGrid from '../../../common/ProzoDataGrid';
import DownloadSampleFile from '../../../common/DownloadSampleFile';
import { ImageLightbox, ProductImageThumbs, Thumb, imagesOf } from '../../../common/ImageLightbox';
import { FormProvider, RHFFileField } from '../../../common/hook-form';
import { apiConstants, postGeneric, post, get } from '../../../common/constants';
import { getMerchant } from '../../../helpers/ReduxHelpers';
import { cellStackSx, primaryTextSx, secondaryTextSx } from '../../orders/list2/cellStyles';
import { pageContainerSx, sectionCardSx, primaryBtnSx, secondaryBtnSx, dataGridWrapSx } from '../../../styles/patterns';
import { statCardSx, statLabelSx, statValueSx, statSubSx } from '../components/stats';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, SUCCESS, WARNING } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { MONO } from '../components/directCellStyles';
import { buildProductMasterCsv, statusTone, MAX_PRODUCT_IMAGES } from '../data/productMaster';
import ProductEditDrawer from '../components/ProductEditDrawer';

const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 };
const fieldSx = {
  '& .MuiInputBase-input': { fontFamily: FONT, fontSize: 12 },
  '& .MuiInputLabel-root': { fontSize: 10 },
};

const TONE = {
  ACTIVE: { bg: `${SUCCESS}14`, fg: SUCCESS },
  INACTIVE: { bg: `${WARNING}1f`, fg: WARNING },
  UNKNOWN: { bg: LIGHT_BG, fg: SUBTLE },
};

/**
 * Images for one product.
 *
 * Save writes a one-row sheet rather than patching the record — see data/productMaster.js for why
 * that is the only write path. Every existing column is resent, so adding a photo cannot blank a
 * weight.
 */
function ManageImagesDialog({ product, merchantId, onClose, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const [images, setImages] = useState(() => imagesOf(product));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewIdx, setViewIdx] = useState(null);

  const full = images.length >= MAX_PRODUCT_IMAGES;

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (full) {
      enqueueSnackbar(`A product can carry at most ${MAX_PRODUCT_IMAGES} images`, { variant: 'warning' });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('attachments[]', file);
      formData.append('folder', 'product_master');
      const res = await post(apiConstants.UPLOAD_IMAGE_NON_EXPIRE, formData);
      const url = res?.data?.[0];
      if (res.status === 200 && url) {
        setImages((prev) => [...prev, url].slice(0, MAX_PRODUCT_IMAGES));
      } else {
        enqueueSnackbar('The upload did not return an image URL', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Image upload failed', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!product.skuCode) {
      enqueueSnackbar('This product has no SKU code, so it cannot be saved', { variant: 'error' });
      return;
    }
    setSaving(true);
    try {
      const csv = buildProductMasterCsv(product, images);
      const file = new File([csv], `${product.skuCode}_images.csv`, { type: 'text/csv' });
      const formData = new FormData();
      formData.append('skuMaster', file);
      const res = await postGeneric(
        `${apiConstants.UPLOAD_SKU_DETAILS}?merchantId=${encodeURIComponent(merchantId)}`,
        formData
      );
      if (res.status === 200) {
        enqueueSnackbar('Images saved', { variant: 'success' });
        onSaved?.();
        onClose();
      } else {
        enqueueSnackbar(typeof res.data === 'string' ? res.data : 'Could not save images', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Could not save images', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY }}>
          Images · {product.name || product.skuCode}
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ ...smallSx, mb: 1.5 }}>
            Up to {MAX_PRODUCT_IMAGES}. These are what a buyer sees on your tracking page
            recommendations, and what a QC agent compares against at the door.
          </Typography>

          <Grid container spacing={1}>
            {images.map((url, i) => (
              <Grid item key={url}>
                <Box sx={{ position: 'relative' }}>
                  <Thumb src={url} size={72} onOpen={() => setViewIdx(i)} />
                  <IconButton
                    size='small'
                    onClick={() => setImages((prev) => prev.filter((u) => u !== url))}
                    aria-label='Remove image'
                    sx={{ position: 'absolute', top: -6, right: -6, bgcolor: '#fff', boxShadow: 1, p: '2px' }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 14, color: SUBTLE }} />
                  </IconButton>
                </Box>
              </Grid>
            ))}
            {!images.length && (
              <Grid item xs={12}>
                <Typography sx={smallSx}>No images yet.</Typography>
              </Grid>
            )}
          </Grid>

          <Button
            component='label'
            size='small'
            disabled={uploading || full}
            startIcon={uploading ? <CircularProgress size={13} /> : <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ ...secondaryBtnSx, mt: 1.5 }}
          >
            {uploading ? 'Uploading…' : 'Add image'}
            <input type='file' accept='image/*' hidden onChange={pick} />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button size='small' sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>
          <LoadingButton
            size='small'
            loading={saving}
            variant='contained'
            sx={primaryBtnSx}
            onClick={save}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {viewIdx !== null && (
        <ImageLightbox images={images} index={viewIdx} onClose={() => setViewIdx(null)} />
      )}
    </>
  );
}

function ProductMaster() {
  const merchant = getMerchant();
  const merchantId = merchant?.id || '';
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(20);
  const [filters, setFilters] = useState([]);
  const [reload, setReload] = useState(0);
  const [showUpload, setShowUpload] = useState(false);
  const [managing, setManaging] = useState(null);
  const [editing, setEditing] = useState(null); // a product, or {} for a new one
  const [lightbox, setLightbox] = useState(null);

  const [skuCode, setSkuCode] = useState('');
  const [name, setName] = useState('');
  const [categories, setCategories] = useState([]);

  /**
 * ProShip's own category vocabulary, so the catalogue and the merchant record agree.
   * A failure here is not worth a message: the picker simply falls back to free text, which is
   * what the field was before it had a list.
   */
  useEffect(() => {
    get(apiConstants.GET_MERCHANT_CATEGORY)
      .then((res) =>
        setCategories(
          (res?.data || []).map((c) => c?.displayName || c?.enum).filter(Boolean)
        )
      )
      .catch(() => setCategories([]));
  }, []);

  /**
   * Always filtered to this merchant.
   *
   * The merchantId condition is prepended rather than left to the caller, so a filter the merchant
   * types can never widen the query beyond their own catalogue.
   */
  const load = useCallback(() => {
    if (!merchantId) return;
    setLoading(true);
    postGeneric(apiConstants.REPORT_GENERIC_SEARCH, {
      collection: 'product_master',
      offset: filters.length ? 0 : page * sizePerPage,
      limit: sizePerPage,
      skipCount: true,
      countOnly: false,
      sort: { field: 'createdDate', direction: 'DESC' },
      andfilter: [{ field: 'merchantId', operator: 'eq', value: merchantId }, ...filters],
    })
      .then((res) => setRows(res?.data?.data || []))
      .catch(() => enqueueSnackbar('Could not load your products', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [merchantId, filters, page, sizePerPage, enqueueSnackbar]);

  // `reload` is a refetch trigger rather than an input the query reads, so it gates the effect and
  // not the callback — which is also what the exhaustive-deps rule expects.
  useEffect(() => { load(); }, [load, reload]);

  const applyFilters = () => {
    const next = [];
    if (skuCode.trim()) next.push({ field: 'skuCode', operator: 'eq', value: skuCode.trim() });
    if (name.trim()) next.push({ field: 'name', operator: 'eq', value: name.trim() });
    setFilters(next);
    setPage(0);
  };

  const resetFilters = () => {
    setSkuCode('');
    setName('');
    setFilters([]);
    setPage(0);
  };

  const withImages = rows.filter((r) => imagesOf(r).length).length;
  const brands = new Set(rows.map((r) => r.brand).filter(Boolean)).size;
  const subCategories = [...new Set(rows.map((r) => r.subCategory).filter(Boolean))].sort();
  const knownCategories = [
    ...new Set([...categories, ...rows.map((r) => r.category).filter(Boolean)]),
  ].sort();
  const categoryCount = new Set(rows.map((r) => r.category).filter(Boolean)).size;

  const columns = [
    {
      field: 'name',
      headerName: 'Product',
      flex: 1.8,
      minWidth: 250,
      sortable: false,
      renderCell: ({ row }) => {
        const imgs = imagesOf(row);
        return (
          <Stack direction='row' spacing={1.25} alignItems='center' sx={{ py: 0.75, width: '100%' }}>
            <Thumb src={imgs[0]} size={42} onOpen={() => setLightbox({ images: imgs, index: 0 })} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={primaryTextSx}>{row.name || '—'}</Typography>
              <Typography sx={{ ...secondaryTextSx, fontFamily: MONO }}>{row.skuCode || '—'}</Typography>
            </Box>
          </Stack>
        );
      },
    },
    {
      field: 'brand',
      headerName: 'Brand / Category',
      flex: 1.2,
      minWidth: 160,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}>
          <Typography sx={primaryTextSx}>{row.brand || '—'}</Typography>
          <Typography sx={secondaryTextSx}>
            {[row.category, row.subCategory].filter(Boolean).join(' · ') || 'Uncategorised'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'weight',
      headerName: 'Weight / Dimensions',
      flex: 1.1,
      minWidth: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}>
          <Typography sx={primaryTextSx}>{row.weight ? `${row.weight} g` : '—'}</Typography>
          <Typography sx={secondaryTextSx}>
            {row.length && row.breath && row.height
              ? `${row.length} × ${row.breath} × ${row.height} cm`
              : 'No dimensions'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'mrp',
      headerName: 'MRP / Tax / HSN',
      minWidth: 145,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={cellStackSx}>
          <Typography sx={primaryTextSx}>
            {row.mrp ? `₹${Number(row.mrp).toLocaleString('en-IN')}` : '—'}
            {row.taxPercent ? (
              <Box component='span' sx={{ color: SUBTLE, fontWeight: WEIGHT.regular }}>
                {' '}
                + {row.taxPercent}% tax
              </Box>
            ) : null}
          </Typography>
          <Typography sx={{ ...secondaryTextSx, fontFamily: MONO }}>{row.hsnCode || 'no HSN'}</Typography>
        </Box>
      ),
    },
    {
      field: 'productStatus',
      headerName: 'Status',
      minWidth: 100,
      sortable: false,
      renderCell: ({ row }) => {
        const tone = TONE[statusTone(row.productStatus)];
        return (
          <Box sx={cellStackSx}>
            <Chip
              size='small'
              label={row.productStatus || 'Not set'}
              sx={{
                height: 19,
                borderRadius: '4px',
                fontFamily: FONT,
                fontSize: SIZE['4xs'],
                fontWeight: WEIGHT.semibold,
                bgcolor: tone.bg,
                color: tone.fg,
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'images',
      headerName: 'Images',
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => {
        const imgs = imagesOf(row);
        return (
          <Stack spacing={0.5} alignItems='flex-start' sx={{ py: 0.75 }}>
            <ProductImageThumbs images={imgs} size={30} empty='None yet' />
            <Button
              size='small'
              startIcon={<AddPhotoAlternateOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => setManaging(row)}
              sx={{ ...secondaryBtnSx, py: 0.1, px: 0.85, fontSize: SIZE['4xs'] }}
            >
              {imgs.length ? `Manage (${imgs.length})` : 'Add'}
            </Button>
          </Stack>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 60,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title='Edit this product'>
          <IconButton size='small' onClick={() => setEditing(row)} aria-label={`Edit ${row.skuCode}`}>
            <EditOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='Your catalogue — weights, dimensions, HSN and images, used to price and pack every order'>
        Product Master
      </PageTitle>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.lg }}>
        <Grid container spacing={1.5}>
          <Grid item xs={6} md={3}>
            <Box sx={statCardSx}>
              <Typography sx={statLabelSx}>Products listed</Typography>
              <Typography sx={statValueSx}>{rows.length.toLocaleString('en-IN')}</Typography>
              <Typography sx={statSubSx}>on this page</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box sx={statCardSx}>
              <Typography sx={statLabelSx}>With images</Typography>
              <Typography sx={statValueSx}>{withImages.toLocaleString('en-IN')}</Typography>
              <Typography sx={statSubSx}>used by QC and recommendations</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box sx={statCardSx}>
              <Typography sx={statLabelSx}>Brands</Typography>
              <Typography sx={statValueSx}>{brands.toLocaleString('en-IN')}</Typography>
              <Typography sx={statSubSx}>on this page</Typography>
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <Box sx={statCardSx}>
              <Typography sx={statLabelSx}>Categories</Typography>
              <Typography sx={statValueSx}>{categoryCount.toLocaleString('en-IN')}</Typography>
              <Typography sx={statSubSx}>on this page</Typography>
            </Box>
          </Grid>
        </Grid>

        {/* ── Filters and upload ── */}
        <Box sx={sectionCardSx}>
          <Box sx={{ display: 'flex', gap: GAP.md, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              size='small'
              label='SKU code'
              value={skuCode}
              onChange={(e) => setSkuCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              sx={{ ...fieldSx, width: 200 }}
            />
            <TextField
              size='small'
              label='Product name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              sx={{ ...fieldSx, width: 220 }}
            />
            <Button size='small' variant='contained' sx={primaryBtnSx} onClick={applyFilters}>
              Filter
            </Button>
            <Button size='small' sx={secondaryBtnSx} onClick={resetFilters}>
              Reset
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              size='small'
              startIcon={showUpload ? <CloseOutlinedIcon sx={{ fontSize: 16 }} /> : <CloudUploadOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={secondaryBtnSx}
              onClick={() => setShowUpload((s) => !s)}
            >
              {showUpload ? 'Close' : 'Bulk upload'}
            </Button>
            <Button
              size='small'
              variant='contained'
              sx={primaryBtnSx}
              startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={() => setEditing({})}
            >
              Add product
            </Button>
          </Box>

          <Collapse in={showUpload}>
            <Box sx={{ mt: 1.75, pt: 1.75, borderTop: `1px solid ${BORDER}` }}>
              <UploadForm
                merchantId={merchantId}
                onDone={() => {
                  setReload((n) => n + 1);
                  setShowUpload(false);
                }}
              />
            </Box>
          </Collapse>
        </Box>

        {!merchantId && (
          <Alert severity='error' sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
            No merchant is selected, so your catalogue cannot be loaded.
          </Alert>
        )}

        {!loading && rows.length === 0 ? (
          <Box sx={{ ...sectionCardSx, textAlign: 'center', py: 5 }}>
            <Inventory2OutlinedIcon sx={{ fontSize: 34, color: SUBTLE, mb: 1 }} />
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>
              {filters.length ? 'Nothing matches that filter' : 'No products yet'}
            </Typography>
            <Typography sx={{ ...smallSx, mb: 1.75 }}>
              {filters.length
                ? 'Try a different SKU code or name.'
                : 'Add one by hand, or upload your catalogue as a sheet. Products also appear here on their own as orders arrive from your channels.'}
            </Typography>
            <Box sx={{ display: 'flex', gap: GAP.md, justifyContent: 'center', flexWrap: 'wrap' }}>
              {filters.length ? (
                <Button size='small' sx={secondaryBtnSx} onClick={resetFilters}>
                  Clear filters
                </Button>
              ) : (
                <>
                  <Button
                    size='small'
                    variant='contained'
                    sx={primaryBtnSx}
                    startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setEditing({})}
                  >
                    Add a product
                  </Button>
                  <Button size='small' sx={secondaryBtnSx} onClick={() => setShowUpload(true)}>
                    Upload a sheet
                  </Button>
                </>
              )}
            </Box>
          </Box>
        ) : (
        <Box sx={dataGridWrapSx}>
          <ProzoDataGrid
            columns={columns}
            rows={rows.map((r, i) => ({ ...r, _rowId: r.skuCode || i }))}
            getRowId={(row) => row._rowId}
            rowCount={rows.length}
            page={page}
            setPage={setPage}
            sizePerPage={sizePerPage}
            setSizePerPage={setSizePerPage}
            paginationMode='server'
            loading={loading}
            disableColumnMenu
            disableColumnFilter
          />
        </Box>
        )}
      </Box>

      <ProductEditDrawer
        product={editing && editing.skuCode ? editing : null}
        merchantId={merchantId}
        categories={knownCategories}
        subCategories={subCategories}
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        onSaved={() => setReload((n) => n + 1)}
      />

      {managing && (
        <ManageImagesDialog
          product={managing}
          merchantId={merchantId}
          onClose={() => setManaging(null)}
          onSaved={() => setReload((n) => n + 1)}
        />
      )}

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </Box>
  );
}

/**
 * Bulk add or update, by sheet.
 *
 * No merchant picker — unlike the admin version there is only one merchant, and it is the person
 * uploading. The sheet upserts on SKU code, so the same file re-uploaded updates rather than
 * duplicating, which is what makes it usable as the merchant's editing tool as well as their import.
 */
function UploadForm({ merchantId, onDone }) {
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const methods = useForm({ defaultValues: { file: null } });
  const { handleSubmit, reset, watch } = methods;
  const file = watch('file');

  const onSubmit = (values) => {
    if (!values.file?.[0]) {
      enqueueSnackbar('Pick a file first', { variant: 'warning' });
      return;
    }
    setBusy(true);
    const formData = new FormData();
    formData.append('skuMaster', values.file[0]);
    postGeneric(`${apiConstants.UPLOAD_SKU_DETAILS}?merchantId=${encodeURIComponent(merchantId)}`, formData)
      .then(() => {
        enqueueSnackbar('Catalogue uploaded', { variant: 'success' });
        reset({ file: null });
        onDone?.();
      })
      .catch((err) =>
        enqueueSnackbar(err.response?.data?.message || 'Upload failed', { variant: 'error' })
      )
      .finally(() => setBusy(false));
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Typography sx={{ ...smallSx, mb: 1.25 }}>
        One row per SKU. Rows are matched on <b>SKU code</b> and updated in place, so re-uploading a
        corrected sheet edits your catalogue rather than duplicating it. Every column is rewritten
        from the sheet — leave one blank and it is cleared.
      </Typography>

      <Box sx={{ display: 'flex', gap: GAP.md, alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ width: 280 }}>
          <RHFFileField name='file' label='Catalogue sheet' />
        </Box>
        <LoadingButton
          type='submit'
          loading={busy}
          size='small'
          variant='contained'
          sx={primaryBtnSx}
          startIcon={<CloudUploadOutlinedIcon sx={{ fontSize: 16 }} />}
          disabled={!file?.[0]}
        >
          Upload
        </LoadingButton>
        <DownloadSampleFile fileName='PRODUCT_MASTER_SAMPLE' label='Download sample' />
      </Box>

      <Box
        sx={{
          mt: 1.5,
          p: 1.25,
          bgcolor: LIGHT_BG,
          borderRadius: RADIUS.md,
          border: `1px solid ${BORDER}`,
        }}
      >
        <Typography sx={smallSx}>
          <Box component='span' sx={{ color: NAVY, fontWeight: WEIGHT.semibold }}>Why it matters: </Box>
          weight and dimensions here are what your shipments are priced and packed on. A SKU without
          them ships on the courier&rsquo;s own measurement, which is where weight disputes start.
          HSN drives the invoice, and images are used by doorstep QC and by tracking-page
          recommendations.
        </Typography>
      </Box>
    </FormProvider>
  );
}

export default withLayout(ProductMaster);
